/**
 * useFeatureFlags — Compliance kill-switch client hook (Feb 2026).
 *
 * Reads `/api/feature-flags` and exposes each flag as a boolean.
 * Client fetches on mount (per user's choice — "client tự fetch mỗi lần
 * load trang"). A module-level cache keeps repeat calls within the same
 * page load from re-hitting the API. Admin toggle dispatches
 * `feature-flags-changed` window event → all mounted hooks re-fetch.
 *
 * Server also enforces the flag (503 `feature_disabled`) so this is
 * purely a UX layer — safe if it fails.
 */
import { useEffect, useRef, useState } from "react";
import { edgeGet } from "../lib/edgeApi";

const DEFAULT_FLAGS = {
  dm_chat: true,
  livestream_chat: true,
  payment: true,
  follow: true,
};

// v17.03 — Last-known cache: khi edge + origin đều 5xx (sự cố PROD 30/07),
// app vẫn có bộ flag người dùng từng nhận thay vì rơi thẳng về DEFAULT_FLAGS
// (có thể khác thực tế đang bật/tắt trên PROD).
const LKF_KEY = "feature_flags_last_known_v1";
function _readLastKnownFlags() {
  try {
    const raw = localStorage.getItem(LKF_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && typeof p === "object") return p;
  } catch { /* corrupt */ }
  return null;
}
function _writeLastKnownFlags(f) {
  try { localStorage.setItem(LKF_KEY, JSON.stringify(f)); } catch { /* quota */ }
}

let cached = _readLastKnownFlags() || null;
let cachedAt = cached ? Date.now() - 60_000 : 0; // đã stale để mount đầu tiên refetch
let inflight = null;
const CACHE_MS = 120_000; // v17.44 — match browser max-age=120 (option C).

async function fetchFlags() {
  const now = Date.now();
  if (cached && now - cachedAt < CACHE_MS) return cached;
  if (inflight) return inflight;
  inflight = edgeGet("/feature-flags", { policyKey: "feature_flags" })
    .then((r) => {
      cached = { ...DEFAULT_FLAGS, ...(r?.data?.flags || {}) };
      cachedAt = Date.now();
      _writeLastKnownFlags(cached);
      return cached;
    })
    .catch(() => {
      // Fail-open v17.03: ưu tiên LAST-KNOWN → mới tới DEFAULT_FLAGS.
      const lk = _readLastKnownFlags();
      cached = { ...DEFAULT_FLAGS, ...(lk || {}) };
      cachedAt = Date.now();
      return cached;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function invalidateFeatureFlagsCache() {
  cached = null;
  cachedAt = 0;
  try {
    window.dispatchEvent(new Event("feature-flags-changed"));
  } catch {
    // noop (SSR guard)
  }
}

export function useFeatureFlags() {
  const [flags, setFlags] = useState(cached || DEFAULT_FLAGS);
  const [loading, setLoading] = useState(!cached);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let alive = true;
    const load = async () => {
      const f = await fetchFlags();
      if (alive && mountedRef.current) {
        setFlags(f);
        setLoading(false);
      }
    };
    load();
    const onChange = () => {
      cached = null;
      cachedAt = 0;
      load();
    };
    window.addEventListener("feature-flags-changed", onChange);
    return () => {
      alive = false;
      mountedRef.current = false;
      window.removeEventListener("feature-flags-changed", onChange);
    };
  }, []);

  return { flags, loading };
}

/** Convenience: read a single flag (defaults to ON if hook not ready). */
export function useFeatureFlag(key) {
  const { flags, loading } = useFeatureFlags();
  return { enabled: !!flags[key], loading };
}
